import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogoIcon } from '../Shared/Icons';
import { auth } from '../../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { Menu, X } from 'lucide-react';

const PublicNavbar: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, []);

    // Helper to determine if a link is active
    const isActive = (path: string) => location.pathname === path;
    const isToolsActive = location.pathname.startsWith('/tools');

    return (
        <header className="bg-gray-900/95 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" onClick={() => setIsMobileMenuOpen(false)}>
                        <LogoIcon className="h-9 w-9" />
                        <span className="text-xl font-extrabold text-white tracking-tight">PocketProf</span>
                    </Link>
                </div>
                
                {/* Desktop Navigation */}
                <nav className="hidden md:flex gap-8">
                    <Link
                        to="/"
                        className={`text-[0.9rem] font-semibold transition-colors ${isActive('/') ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Home
                    </Link>
                    <Link
                        to="/features"
                        className={`text-[0.9rem] font-semibold transition-colors ${isActive('/features') ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Features
                    </Link>
                    <Link
                        to="/pricing"
                        className={`text-[0.9rem] font-semibold transition-colors ${isActive('/pricing') ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Pricing
                    </Link>
                    <Link
                        to="/tools"
                        className={`text-[0.9rem] font-semibold transition-colors ${isToolsActive ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Free Tools
                    </Link>
                    <Link
                        to="/tutorials"
                        className={`text-[0.9rem] font-semibold transition-colors ${isActive('/tutorials') ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Tutorials
                    </Link>
                    <Link
                        to="/contact"
                        className={`text-[0.9rem] font-semibold transition-colors ${isActive('/contact') ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Contact
                    </Link>
                </nav>
                
                {/* Action buttons & Mobile Toggle */}
                <div className="flex items-center gap-4">
                    <div className="hidden md:block">
                        {user ? (
                            <Link to="/app" className="px-5 py-2.5 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors shadow-md shadow-primary/20">
                                Dashboard
                            </Link>
                        ) : (
                            <Link to="/login" className="px-5 py-2.5 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors shadow-md shadow-primary/20">
                                Login
                            </Link>
                        )}
                    </div>
                    
                    {/* Mobile menu button */}
                    <button 
                        className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg focus:outline-none transition-colors"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
                <div className="md:hidden bg-gray-900 border-t border-gray-800 absolute w-full left-0 shadow-xl">
                    <div className="px-4 py-4 space-y-2">
                        <Link
                            to="/"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${isActive('/') ? 'text-white bg-gray-800' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
                        >
                            Home
                        </Link>
                        <Link
                            to="/features"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${isActive('/features') ? 'text-white bg-gray-800' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
                        >
                            Features
                        </Link>
                        <Link
                            to="/pricing"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${isActive('/pricing') ? 'text-white bg-gray-800' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
                        >
                            Pricing
                        </Link>
                        <Link
                            to="/tools"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${isToolsActive ? 'text-white bg-gray-800' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
                        >
                            Free Tools
                        </Link>
                        <Link
                            to="/tutorials"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${isActive('/tutorials') ? 'text-white bg-gray-800' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
                        >
                            Tutorials
                        </Link>
                        <Link
                            to="/contact"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${isActive('/contact') ? 'text-white bg-gray-800' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
                        >
                            Contact
                        </Link>
                        
                        <div className="pt-4 mt-4 border-t border-gray-800">
                            {user ? (
                                <Link 
                                    to="/app" 
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="block w-full text-center px-5 py-3.5 rounded-xl bg-primary text-white font-semibold text-base hover:bg-primary-hover transition-colors shadow-md shadow-primary/20"
                                >
                                    Dashboard
                                </Link>
                            ) : (
                                <Link 
                                    to="/login" 
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="block w-full text-center px-5 py-3.5 rounded-xl bg-primary text-white font-semibold text-base hover:bg-primary-hover transition-colors shadow-md shadow-primary/20"
                                >
                                    Login
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default PublicNavbar;
